import { Link } from "react-router-dom";
import { BadgeCheck, Banknote, ShieldCheck, LineChart } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const QUALIOPI_LOGO =
  "https://static.wixstatic.com/media/847d29_375a6ab7bd7a40578f98fbff828e7d32~mv2.png";

const POINTS = [
  {
    icon: ShieldCheck,
    t: "Qu'est-ce que Qualiopi ?",
    d: "La marque de certification qualité délivrée par l'État français aux organismes de formation dont les processus sont audités selon le Référentiel National Qualité.",
  },
  {
    icon: LineChart,
    t: "Ce que ça garantit",
    d: "Un fonctionnement audité : accueil des stagiaires, adaptation des séances, suivi pédagogique, recueil des appréciations et amélioration continue.",
  },
  {
    icon: Banknote,
    t: "Ce que ça débloque",
    d: "L'accès aux financements de la formation professionnelle — le cursus praticien OJANZU est finançable par les opérateurs de compétences.",
  },
  {
    icon: BadgeCheck,
    t: "Notre engagement",
    d: "Une transmission exigeante et humaine, contrôlée de l'intérieur comme de l'extérieur, pour des praticien·ne·s sûrs de leur pratique.",
  },
];

export default function Certification() {
  const { tr } = useLang();
  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="certification-page">
      <div className="relative overflow-hidden bg-deep/50 pb-24 pt-14">
        <div className="absolute inset-0 caustic-glow" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <SectionLabel>{tr("Certification")}</SectionLabel>
              <h1 className="mt-5 font-serif text-4xl md:text-5xl font-light leading-tight text-foam">
                {tr("Une école certifiée.")}
              </h1>
              <div className="img-clip-a mt-10 inline-flex flex-col items-center rounded-3xl bg-foam p-10 shadow-[0_0_50px_rgba(56,189,248,0.25)]">
                <img
                  src={QUALIOPI_LOGO}
                  alt="Logo officiel de la certification Qualiopi"
                  loading="lazy"
                  className="h-40 w-auto object-contain"
                  data-testid="qualiopi-logo"
                />
                <p className="mt-6 max-w-[260px] text-center text-xs leading-relaxed text-slate-600">
                  {tr("Certification qualité délivrée au titre des actions de formation.")}
                </p>
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              {POINTS.map((p, i) => (
                <Reveal key={p.t} delay={0.08 * i} className="border-b border-aqua/12 first:border-t">
                  <div className="flex gap-6 py-8 group">
                    <span className="mt-1 grid h-12 w-12 shrink-0 place-items-center rounded-full border border-aqua/30 text-aqua transition-all duration-500 group-hover:bg-aqua/10 group-hover:shadow-[0_0_24px_rgba(56,189,248,0.3)]">
                      <p.icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-serif text-2xl text-foam">{tr(p.t)}</h3>
                      <p className="mt-2 max-w-xl text-sm leading-relaxed text-foam/60">{tr(p.d)}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.3}>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/formations"
                    data-testid="certification-formations-link"
                    className="rounded-full bg-aqua px-7 py-3.5 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam hover:shadow-[0_0_36px_rgba(56,189,248,0.5)]"
                  >
                    {tr("Voir les formations finançables")}
                  </Link>
                  <Link
                    to="/contact"
                    data-testid="certification-contact-link"
                    className="rounded-full border border-foam/25 px-7 py-3.5 text-sm font-medium text-foam/85 transition-colors duration-300 hover:border-foam/60 hover:text-foam"
                  >
                    {tr("Poser une question")}
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
